import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { instantAtX, SLOTS_PER_DAY } from '../timeline'
import { type CreateTarget, createTarget, selectionRange } from './timeline-create'
import { DRAG_THRESHOLD_PX } from './timeline-drag'

/** Marks every bar; a press on one is that bar's, never the empty lane's. */
export const BAR_ATTRIBUTE = 'data-timeline-bar'

/** The ghost for a selection in progress. */
export interface CreateGhost {
  laneIndex: number
  startsAt: Date
  endsAt: Date
}

export interface LaneCreateHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void
}

interface Options {
  boundaries: readonly number[]
  categoryId: string
  canCreate: boolean
  onCreate: (target: CreateTarget) => void
}

interface Session {
  pointerId: number
  laneIndex: number
  vehicleId: string
  originX: number
  anchor: Date
  moved: boolean
}

/**
 * The empty-lane selection: press on a vehicle lane away from any bar, drag
 * across its cells, release to open the new-booking screen for that range. A
 * release inside the drag threshold does nothing. Escape cancels. A finger
 * never starts it (OPS-1): on a touch screen a swipe across the lanes pans
 * the days, and a new booking is a tap on "Nuova prenotazione".
 */
export function useTimelineCreate({ boundaries, categoryId, canCreate, onCreate }: Options) {
  const session = useRef<Session | null>(null)
  const [ghost, setGhost] = useState<CreateGhost | null>(null)

  const reset = useCallback(() => {
    session.current = null
    setGhost(null)
  }, [])

  useEffect(() => {
    if (!ghost) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') reset()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [ghost, reset])

  const instantAt = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const rect = event.currentTarget.getBoundingClientRect()
      return instantAtX(event.clientX - rect.left, rect.width, boundaries, SLOTS_PER_DAY)
    },
    [boundaries],
  )

  const laneHandlers = useCallback(
    (vehicleId: string | null, laneIndex: number): LaneCreateHandlers | null => {
      if (!canCreate || vehicleId === null) return null
      return {
        onPointerDown: (event) => {
          if (event.button !== 0) return
          // A finger on an empty lane pans the days (OPS-1): only a mouse or a pen selects.
          if (event.pointerType === 'touch') return
          if (event.target instanceof Element && event.target.closest(`[${BAR_ATTRIBUTE}]`)) return
          session.current = {
            pointerId: event.pointerId,
            laneIndex,
            vehicleId,
            originX: event.clientX,
            anchor: instantAt(event),
            moved: false,
          }
          event.currentTarget.setPointerCapture(event.pointerId)
        },
        onPointerMove: (event) => {
          const active = session.current
          if (!active || active.pointerId !== event.pointerId) return
          if (!active.moved && Math.abs(event.clientX - active.originX) <= DRAG_THRESHOLD_PX) return
          active.moved = true
          const range = selectionRange(active.anchor, instantAt(event))
          setGhost(range ? { laneIndex: active.laneIndex, ...range } : null)
        },
        onPointerUp: (event) => {
          const active = session.current
          if (!active || active.pointerId !== event.pointerId) return
          const range = active.moved ? selectionRange(active.anchor, instantAt(event)) : null
          const vehicle = active.vehicleId
          reset()
          if (range) onCreate(createTarget({ categoryId, vehicleId: vehicle, ...range }))
        },
        onPointerCancel: (event) => {
          if (session.current?.pointerId === event.pointerId) reset()
        },
      }
    },
    [canCreate, categoryId, instantAt, onCreate, reset],
  )

  return { ghost, laneHandlers }
}
